import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse } from '@angular/common/http';
import { Observable, from, forkJoin, throwError, EMPTY } from 'rxjs';
import { switchMap, catchError, takeUntil } from 'rxjs/operators';
import { OktaAuthService } from '../../services/okta-auth.service';
import { GuestTokenStore } from '../guest/guest-token.store';
import { IdentitySwitchService } from '../../services/identity-switch.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;

  constructor(private oktaService: OktaAuthService,
              private guestTokenStore: GuestTokenStore,
              private identitySwitch: IdentitySwitchService) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // Guest + public-post surfaces: these callers have no Okta session. Redeem and the PublicPost
    // reads are anonymous; every other guest call carries the Guest JWT. Handle here and skip the
    // Okta logic entirely so it never tries (and fails) to attach an Okta token for a guest.
    if (request.url.includes('/api/GuestAccess/') || request.url.includes('/api/GuestPost/') || request.url.includes('/api/PublicPost/')) {
      const guestToken = this.guestTokenStore.token;
      const guestReq = guestToken
        ? request.clone({ setHeaders: { Authorization: `Bearer ${guestToken}` } })
        : request;
      return next.handle(guestReq);
    }

    const allowedOrigins = ['http://localhost', 'https://localhost', 'https://10.0.2.2', 'https://theapppilot.nhaschools.com/api', 'https://app.nhaschools.com/api', 'https://theapp.nhaschools.com/api', 'https://app-dev.nhaschools.com/api', 'https://app-qa.nhaschools.com/api'];
    if (!!allowedOrigins.find(origin => request.url.includes(origin))) {
      // Across an account switch this document is still running as the old identity while the
      // server has already moved to the new one, so anything it sends lands as the wrong user —
      // guardian calls as staff, staff-only calls as the guardian (403 on AppAccess_*). Hold every
      // app call for the duration, except the identity-changing calls themselves. The page is
      // reloading (or about to adopt the new profile), so a silent complete is the right outcome.
      const isIdentitySwitchRequest = request.url.includes('/GuardianLink/SwitchToGuardianAsync') ||
        request.url.includes('/UserProfile/ClearImpersonationAsync') ||
        request.url.includes('/UserProfile/ImpersonateUserAsync');

      if (!isIdentitySwitchRequest) {
        if (this.identitySwitch.isSwitching) {
          return EMPTY;
        }
        // A switch starting mid-flight unsubscribes here, which aborts the XHR, so a call issued as
        // the old identity can't land on the server after the identity has already changed.
        return this.handleAppRequest(request, next).pipe(takeUntil(this.identitySwitch.aborted$));
      }
      return this.handleAppRequest(request, next);
    } else {
      return next.handle(request);
    }
  }

  private handleAppRequest(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // Check if this request is going to the Grades controller
    const isParentPortalRequest = request.url.includes('/Grades/') || 
    request.url.includes('/Attendance/') || 
    request.url.includes('/Student/GetParentPortalStudentByUserIdAsync/') || 
    request.url.includes('/guardian/GetStudentsByGuardianAsync') || 
    request.url.includes('/guardian/GetVolunteerRecordStatusAsync') || 
    request.url.includes('/guardian/GetTodosByGuardianAsync') || 
    request.url.includes('/Student/GetCollegeReadinessDashboard/') || 
    request.url.includes('/notification/GetPortalNotificationsAsync');

    if (isParentPortalRequest) {
      // For Grades requests, get both accessToken and idToken
      return forkJoin({
        accessToken: from(this.oktaService.getOktaAuth().tokenManager.get('accessToken')),
        idToken: from(this.oktaService.getOktaAuth().tokenManager.get('idToken'))
      }).pipe(
        switchMap(tokens => {
          const accessTokenValue = (tokens.accessToken && 'accessToken' in tokens.accessToken)
            ? (tokens.accessToken as any).accessToken
            : undefined;
          const idTokenValue = (tokens.idToken && 'idToken' in tokens.idToken)
            ? (tokens.idToken as any).idToken
            : undefined;

          let authReq = request;
          if (accessTokenValue) {
            authReq = authReq.clone({ setHeaders: { Authorization: `Bearer ${accessTokenValue}` } });
          }
          if (idTokenValue) {
            authReq = authReq.clone({ setHeaders: { 'X-Okta-IdToken': idTokenValue } });
          }
          return next.handle(authReq);
        })
      );
    } else {
      // For all other requests, just get accessToken
      return from(this.oktaService.getOktaAuth().tokenManager.get('accessToken')).pipe(
        switchMap(token => {
          const accessToken = (token && 'accessToken' in token) ? (token as any).accessToken : undefined;
          const authReq = accessToken
            ? request.clone({ setHeaders: { Authorization: `Bearer ${accessToken}` } })
            : request;
          return next.handle(authReq).pipe(
            catchError((error: HttpErrorResponse) => {
              // Handle 401 errors with token refresh retry
              if (error.status === 401 && !request.url.includes('/token') && !this.isRefreshing) {
                return this.handle401Error(request, next);
              }
              return throwError(() => error);
            })
          );
        })
      );
    }
  }

  /**
   * Handle 401 errors by attempting to refresh the access token
   * If refresh succeeds, retry the original request with the new token
   * If refresh fails, propagate the error
   */
  private handle401Error(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    this.isRefreshing = true;

    return from(this.oktaService.ensureFreshAccessToken()).pipe(
      switchMap(success => {
        this.isRefreshing = false;

        if (success) {
          // Token refreshed successfully - retry request with new token
          return from(this.oktaService.getOktaAuth().tokenManager.get('accessToken')).pipe(
            switchMap(token => {
              const accessToken = (token && 'accessToken' in token) ? (token as any).accessToken : undefined;

              if (accessToken) {
                const retryReq = request.clone({
                  setHeaders: { Authorization: `Bearer ${accessToken}` }
                });
                return next.handle(retryReq);
              }

              // No access token after refresh - fail gracefully
              return throwError(() => new HttpErrorResponse({
                error: 'Token refresh succeeded but no access token available',
                status: 401,
                statusText: 'Unauthorized'
              }));
            })
          );
        } else {
          // Token refresh failed - propagate original 401 error
          return throwError(() => new HttpErrorResponse({
            error: 'Token refresh failed',
            status: 401,
            statusText: 'Unauthorized'
          }));
        }
      }),
      catchError(error => {
        this.isRefreshing = false;
        return throwError(() => error);
      })
    );
  }
}
